(() => {
  window.ACMedia?.destroy?.();

  const selector = '[data-ac-media-video]';
  const controllers = new Map();
  const choices = new WeakMap();
  const listeners = [];
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let pageLoaded = document.readyState === 'complete';
  const on = (target, type, listener, options) => {
    target.addEventListener(type, listener, options);
    listeners.push(() => target.removeEventListener(type, listener, options));
  };
  const reduced = () => document.body?.dataset.acMotion === 'reduced' || mediaQuery.matches;
  const allowAutoplay = () => !reduced();

  const loadPoster = (video) => {
    if (!video?.dataset.poster) return;
    video.poster = video.dataset.poster;
    delete video.dataset.poster;
  };

  const loadSources = (video, explicit = false) => {
    loadPoster(video);
    if (!video || (!explicit && (!allowAutoplay() || !pageLoaded))) return false;
    const sources = [...video.querySelectorAll('source[data-src]')];
    if (!sources.length) return false;
    for (const source of sources) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    }
    video.load();
    return true;
  };

  class VideoController {
    constructor(video) {
      this.video = video;
      this.visible = false;
      this.active = true;
      this.removers = [];
      this.button = video.id
        ? [...document.querySelectorAll('[data-ac-media-toggle]')].find((button) => button.getAttribute('aria-controls') === video.id)
        : null;
      this.controls = this.button?.closest('[data-ac-media-controls]');
      this.soundButton = this.controls?.querySelector('[data-ac-media-sound]');
      const listen = (target, type, listener) => {
        target.addEventListener(type, listener);
        this.removers.push(() => target.removeEventListener(type, listener));
      };
      if (this.button) {
        listen(this.button, 'click', () => {
          if (!video.paused) {
            choices.set(video, 'pause');
            video.pause();
          } else {
            choices.set(video, 'play');
            if (video.error) video.load();
            this.play(true);
          }
          this.updateControls();
        });
        for (const type of ['play', 'pause', 'ended', 'error', 'volumechange']) listen(video, type, () => this.updateControls());
        if (this.controls) this.controls.hidden = false;
        this.updateControls();
      }
      if (this.soundButton) listen(this.soundButton, 'click', () => {
        const unmute = video.muted;
        if (unmute) for (const other of controllers.keys()) if (other !== video) other.muted = true;
        video.muted = !unmute;
        if (unmute && video.paused) {
          choices.set(video, 'play');
          this.play(true);
        }
        this.updateControls();
      });
      this.observer = 'IntersectionObserver' in window
        ? new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.sync();
        }, { threshold: 0.01 })
        : null;
      this.observer?.observe(video);
      if (!this.observer) this.visible = true;
      this.sync();
    }

    updateControls() {
      if (!this.button) return;
      const playing = !this.video.paused && !this.video.ended;
      this.button.toggleAttribute('data-ac-media-playing', playing);
      this.button.setAttribute('aria-label', playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel);
      if (this.soundButton) {
        const audible = !this.video.muted && this.video.volume > 0;
        this.soundButton.toggleAttribute('data-ac-media-audible', audible);
        this.soundButton.setAttribute('aria-label', audible ? this.soundButton.dataset.muteLabel : this.soundButton.dataset.unmuteLabel);
      }
    }

    play(explicit = false) {
      if (!this.active || !this.video.isConnected || document.hidden || choices.get(this.video) === 'pause' || (!explicit && !allowAutoplay())) return;
      if (this.video.querySelector('source[data-src]') && !loadSources(this.video, explicit)) return;
      this.video.play().catch(() => this.updateControls());
    }

    sync() {
      if (this.visible) loadPoster(this.video);
      const choice = choices.get(this.video);
      if (!this.visible || document.hidden || choice === 'pause' || (!allowAutoplay() && choice !== 'play')) {
        if (!this.visible || document.hidden) this.video.muted = true;
        this.video.pause();
        return;
      }
      if (choice === 'play' || this.video.dataset.acMediaAutoplay === 'true') this.play(choice === 'play');
    }

    destroy() {
      this.active = false;
      this.observer?.disconnect();
      this.video.pause();
      this.removers.splice(0).reverse().forEach((remove) => remove());
      if (this.controls) this.controls.hidden = true;
    }
  }

  const init = (scope = document) => {
    const videos = [...scope.querySelectorAll(selector)];
    if (scope instanceof Element && scope.matches(selector)) videos.unshift(scope);
    videos.forEach((video) => {
      if (!controllers.has(video)) controllers.set(video, new VideoController(video));
    });
  };
  const syncAll = () => controllers.forEach((controller) => controller.sync());
  on(window, 'load', () => { pageLoaded = true; syncAll(); }, { once: true });
  const destroyWithin = (scope = document) => {
    [...controllers.entries()].forEach(([video, controller]) => {
      if (scope === document || scope === video || scope.contains(video)) {
        controller.destroy();
        controllers.delete(video);
      }
    });
  };
  const destroy = () => {
    destroyWithin(document);
    listeners.splice(0).reverse().forEach((remove) => remove());
  };

  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', () => init(), { once: true });
  else init();
  on(document, 'visibilitychange', syncAll);
  const syncMotion = () => {
    if (reduced()) {
      controllers.forEach((controller, video) => {
        if (choices.get(video) === 'play') choices.set(video, 'pause');
      });
    }
    syncAll();
  };
  on(mediaQuery, 'change', syncMotion);
  const motionObserver = new MutationObserver(syncMotion);
  motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
  listeners.push(() => motionObserver.disconnect());
  on(document, 'shopify:section:load', (event) => init(event.target));
  on(document, 'shopify:section:unload', (event) => destroyWithin(event.target));
  on(window, 'pagehide', (event) => {
    if (event.persisted) controllers.forEach((controller) => { controller.video.muted = true; controller.video.pause(); });
    else destroy();
  });
  on(window, 'pageshow', (event) => {
    if (event.persisted) syncAll();
  });
  window.ACMedia = {
    destroy,
    init,
    sync: syncAll,
    play(video) {
      controllers.get(video)?.play();
    },
    preload(video) {
      if (!pageLoaded || !video) return;
      video.preload = 'auto';
      // Loading ahead never starts playback or changes the user's sound choice.
      loadSources(video, true);
    },
  };
})();
